import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Edit2, Camera, Settings, Zap, Bell, RefreshCw } from 'lucide-react';
import { locationService } from '../../services';
import LocationActionsTab from './LocationActionsTab';
import AlarmManagementTab from '../../components/alarms/AlarmManagementTab';
import type { Location } from '../../types';
import { nvrHttpUrl } from '../../utils/nvrAccess';

type TabType = 'details' | 'actions' | 'alarms';

const ViewLocationPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchLocation(parseInt(id));
    }
  }, [id]);

  const fetchLocation = async (locationId: number) => {
    try {
      setIsLoading(true);
      const location = await locationService.getLocationById(locationId);
      console.log('🔍 Location API Response:', location);
      console.log('🔑 Cloud Provider:', location.cloud_provider);
      console.log('🔑 Cloud API Key Set:', location.cloud_api_key_set);
      console.log('🔑 API Key (old):', location.api_key);
      console.log('🔑 App Secret (old):', location.app_secret);
      setLocation(location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch location');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncCameras = async () => {
    if (!location) return;

    setIsSyncing(true);
    setError(null);
    setSyncSuccess(null);

    try {
      const response = await locationService.syncCloudCameras(location.ycl_id);
      const { sync_result } = response;

      if (sync_result.success) {
        const { cameras_synced, cameras_added, cameras_updated, cameras_removed, cameras_failed } = sync_result;
        const parts = [];
        if (cameras_added > 0) parts.push(`${cameras_added} added`);
        if (cameras_updated > 0) parts.push(`${cameras_updated} updated`);
        if (cameras_removed > 0) parts.push(`${cameras_removed} removed`);
        if (cameras_failed > 0) parts.push(`${cameras_failed} failed`);
        
        setSyncSuccess(
          `✅ Cloud sync completed! ${cameras_synced} camera${cameras_synced !== 1 ? 's' : ''} in cloud` +
          (parts.length > 0 ? ` (${parts.join(', ')})` : '')
        );
        // Refresh location data to get updated camera list
        await fetchLocation(location.ycl_id);
      } else {
        setError(sync_result.message || 'Cloud sync failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync cameras from cloud');
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" text="Loading location..." />
        </div>
      </MainLayout>
    );
  }

  if (!location) {
    return (
      <MainLayout>
        <Alert variant="error" message="Location not found" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/locations')}
            >
              Back to Locations
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-text-primary">{location.ycl_name}</h1>
              <p className="text-text-muted">{location.ycl_code}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Show Sync Cameras button for cloud-enabled locations with credentials */}
            {location.cloud_provider && (location.cloud_api_key_set || location.api_key) && (
              <Button
                variant="secondary"
                leftIcon={<RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />}
                onClick={handleSyncCameras}
                disabled={isSyncing}
              >
                {isSyncing ? 'Syncing...' : 'Sync Cameras'}
              </Button>
            )}
            <Button
              variant="primary"
              leftIcon={<Edit2 size={18} />}
              onClick={() => navigate(`/locations/${location.ycl_id}/edit`)}
            >
              Edit Location
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
        {syncSuccess && <Alert variant="success" message={syncSuccess} onClose={() => setSyncSuccess(null)} />}

        {/* Tabs Navigation */}
        <div className="border-b border-surface-secondary">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'details'
                  ? 'border-accent-primary text-accent-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:border-surface-hover'
              }`}
            >
              <Settings size={16} />
              Location Details
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'actions'
                  ? 'border-accent-primary text-accent-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:border-surface-hover'
              }`}
            >
              <Zap size={16} />
              Actions
            </button>
            <button
              onClick={() => setActiveTab('alarms')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'alarms'
                  ? 'border-accent-primary text-accent-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:border-surface-hover'
              }`}
            >
              <Bell size={16} />
              Cloud Alarms
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'actions' ? (
          <LocationActionsTab locationId={location.ycl_id} />
        ) : activeTab === 'alarms' ? (
          <AlarmManagementTab locationId={location.ycl_id} />
        ) : (
          <>
        {/* Basic Information */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Location Name</label>
              <p className="text-text-primary font-medium">{location.ycl_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Location Code</label>
              <p className="text-text-primary font-mono">{location.ycl_code}</p>
            </div>
            {location.ycl_type && (
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Type</label>
                <p className="text-text-primary">{location.ycl_type}</p>
              </div>
            )}
            {location.ycl_company && (
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Company ID</label>
                <p className="text-text-primary">{location.ycl_company}</p>
              </div>
            )}
            {location.ycl_description && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-text-muted mb-1 block">Description</label>
                <p className="text-text-primary">{location.ycl_description}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Address Information */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Address Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {location.ycl_address && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-text-muted mb-1 block">Street Address</label>
                <p className="text-text-primary">{location.ycl_address}</p>
              </div>
            )}
            {location.ycl_city && (
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">City</label>
                <p className="text-text-primary">{location.ycl_city}</p>
              </div>
            )}
            {location.ycl_province && (
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Province/State</label>
                <p className="text-text-primary">{location.ycl_province}</p>
              </div>
            )}
            {location.ycl_postal_code && (
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Postal Code</label>
                <p className="text-text-primary">{location.ycl_postal_code}</p>
              </div>
            )}
            {location.ycl_caller_id && (
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Caller ID</label>
                <p className="text-text-primary">{location.ycl_caller_id}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Coordinates</label>
              <p className="text-text-primary">
                {location.ycl_latitude && location.ycl_longitude
                  ? `${location.ycl_latitude.toFixed(6)}, ${location.ycl_longitude.toFixed(6)}`
                  : 'Not set'}
              </p>
            </div>
          </div>
        </Card>

        {/* NVR Configuration */}
        {(location.nvr_ip || location.nvr_type) && (
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">NVR Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {location.nvr_ip && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">NVR IP Address</label>
                  <p className="text-text-primary font-mono">{location.nvr_ip}</p>
                </div>
              )}
              {location.nvr_port && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">NVR Port (ISAPI)</label>
                  <p className="text-text-primary">{location.nvr_port}</p>
                </div>
              )}
              {location.nvr_rtsp_port && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">NVR RTSP Port</label>
                  <p className="text-text-primary">{location.nvr_rtsp_port}</p>
                </div>
              )}
              {location.external_hostname && <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">NVR HTTP/ISAPI address</label>
                <p className="text-text-primary font-mono break-all">{(() => {
                  try { return nvrHttpUrl(location.external_hostname!, location.external_http_port, location.nvr_port); }
                  catch { return 'Check the hostname and HTTP/ISAPI port in location settings.'; }
                })()}</p>
              </div>}
              {location.nvr_username && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">NVR Username</label>
                  <p className="text-text-primary">{location.nvr_username}</p>
                </div>
              )}
              {location.nvr_type && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">NVR Type</label>
                  <p className="text-text-primary">{location.nvr_type}</p>
                </div>
              )}
              {location.nvr_model && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">NVR Model</label>
                  <p className="text-text-primary">{location.nvr_model}</p>
                </div>
              )}
              {location.nvr_channel_count && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">Channel Count</label>
                  <p className="text-text-primary">{location.nvr_channel_count}</p>
                </div>
              )}
              {location.nvr_connection_status && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">Connection Status</label>
                  <Badge variant={location.nvr_connection_status === 'CONNECTED' ? 'success' : 'danger'}>
                    {location.nvr_connection_status}
                  </Badge>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Transcoding Configuration */}
        {location.transcoding_enabled && (
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Transcoding Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Status</label>
                <Badge variant="success">Enabled</Badge>
              </div>
              {location.transcoding_device_ip && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">Device IP</label>
                  <p className="text-text-primary font-mono">{location.transcoding_device_ip}</p>
                </div>
              )}
              {location.transcoding_device_port && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">Device Port</label>
                  <p className="text-text-primary">{location.transcoding_device_port}</p>
                </div>
              )}
              {location.transcoding_device_api_port && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">API Port</label>
                  <p className="text-text-primary">{location.transcoding_device_api_port}</p>
                </div>
              )}
              {location.transcoding_device_status && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">Device Status</label>
                  <Badge variant={location.transcoding_device_status === 'ONLINE' ? 'success' : 'danger'}>
                    {location.transcoding_device_status}
                  </Badge>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* External Access Configuration */}
        {location.external_access_enabled && (
          <Card padding="lg" className="border-l-4 border-l-green-500">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              External NVR Access {location.cloud_provider && '(Cloud + NVR)'}
            </h3>
            {location.cloud_provider && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-200">
                  <strong>🔗 Hybrid Setup:</strong> This cloud location ({location.cloud_provider}) also has external NVR access configured. 
                  Used for fetching intelligent metadata (behavior analysis, face detection, intrusion zones) and accurate playback via ISAPI.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-text-muted mb-1 block">Status</label>
                <Badge variant="success">Enabled</Badge>
              </div>
              {location.external_hostname && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">External Hostname</label>
                  <p className="text-text-primary font-mono">{location.external_hostname}</p>
                </div>
              )}
              {location.nvr_username && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">
                    {location.cloud_provider ? 'NVR ISAPI Username' : 'NVR Username'}
                  </label>
                  <p className="text-text-primary font-mono">{location.nvr_username}</p>
                </div>
              )}
              {location.nvr_password_encrypted && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">
                    {location.cloud_provider ? 'NVR ISAPI Password' : 'NVR Password'}
                  </label>
                  <p className="text-text-primary">••••••••</p>
                  <p className="text-xs text-text-muted mt-1">Credentials configured</p>
                </div>
              )}

            </div>
          </Card>
        )}

        {(location.external_transcoding_port || location.external_rtsp_port) && <Card padding="lg">
          <details>
            <summary className="cursor-pointer text-sm font-semibold text-text-primary">Other streaming services</summary>
            <p className="my-3 text-sm text-text-secondary">Event recording downloads use the NVR HTTP/ISAPI address.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">              {location.external_transcoding_port && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">External Transcoding Port</label>
                  <p className="text-text-primary">{location.external_transcoding_port}</p>
                </div>
              )}
              {location.external_rtsp_port && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-1 block">External RTSP Port</label>
                  <p className="text-text-primary">{location.external_rtsp_port}</p>
                </div>
              )}</div>
          </details>
        </Card>}

        {/* Settings */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Events</label>
              <Badge variant={location.ycl_events_enabled ? 'success' : 'default'}>
                {location.ycl_events_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Entry Logging</label>
              <Badge variant={location.ycl_entry_logging ? 'success' : 'default'}>
                {location.ycl_entry_logging ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Status</label>
              <Badge variant={location.status ? 'success' : 'danger'}>
                {location.status ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">Default Location</label>
              <Badge variant={location.ycl_defaut_location ? 'success' : 'default'}>
                {location.ycl_defaut_location ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Timezone Configuration */}
        {location.location_timezone && (
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Location Timezone</h3>
            <div>
              <label className="text-sm font-medium text-text-muted mb-1 block">NVR Timezone</label>
              <Badge variant="default" className="text-sm">
                {location.location_timezone}
              </Badge>
              <p className="text-xs text-text-secondary mt-2">
                Events are stored in this timezone for accurate playback and metadata retrieval
              </p>
            </div>
          </Card>
        )}

        {/* Associated Cameras */}
        {location.camera_details && location.camera_details.length > 0 && (
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Associated Cameras</h3>
            <div className="border border-border rounded-lg divide-y divide-border">
              {location.camera_details.map((camera) => (
                <div key={camera.id} className="p-3 flex items-center justify-between hover:bg-surface-secondary transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      camera.status ? 'bg-status-success/10' : 'bg-surface-secondary'
                    }`}>
                      <Camera size={16} className={camera.status ? 'text-status-success' : 'text-text-muted'} />
                    </div>
                    <span className="text-text-primary">{camera.name}</span>
                  </div>
                  <Badge variant={camera.status ? 'success' : 'danger'} dot size="sm">
                    {camera.status ? 'Online' : 'Offline'}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        )}

        {(!location.camera_details || location.camera_details.length === 0) && (
          <Card padding="lg">
            <div className="text-center py-8">
              <Camera size={48} className="text-text-muted mx-auto mb-3" />
              <p className="text-text-muted">No cameras assigned to this location</p>
            </div>
          </Card>
        )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default ViewLocationPage;
